import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, DialogContent, DialogTitle } from '@mui/material';

import {
    loadAppointmentTypeDetailsByContractId,
    onSetCurrentContractId
} from '../../../store/directusService';
import { selectUsersForNames } from '../../../store/userSelectors';
import { trimEntries } from '../../../utils/objectUtils';
import { hasRole } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import SingleSelect from '../../formElements/SingleSelect';

import FilterContentAppointment from './FilterContentAppointment';
import { validate } from './validateTimelineFilters';

import '../../../commonStyles/muiStyles.css';
import form from '../../../commonStyles/formStyles.module.css';
import modal from '../../../commonStyles/modal.module.css';

const TimelineFilterEditor = ({
    rows,
    isOpen,
    filterRoles,
    onCancel,
    timelineFilterDetails,
    onPresentFilters
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [selects, setSelects] = useState({});
    const [errors, setErrors] = useState({});
    const [selectedTimelineFilter, setSelectedTimelineFilter] = useState({});
    const [type, setType] = useState('');
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
    const [appointmentTypes, setAppointmentTypes] = useState([]);
    const [appointmentUsers, setAppointmentUsers] = useState([]);
    const [attendanceStatuses, setAttendanceStatuses] = useState([]);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const appointmentTypesByContractId = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetailsByContractId
    );
    const attendanceDetails = useSelector(
        (state) => state.entities.directusService.attendanceDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const users = useSelector(selectUsersForNames);

    // USEEFFECTS
    useEffect(() => {
        if (!(currentParticipant.contractId in appointmentTypesByContractId)) {
            dispatch(onSetCurrentContractId(currentParticipant.contractId));
            dispatch(loadAppointmentTypeDetailsByContractId([currentParticipant.contractId]));
        }
    }, []);

    useEffect(() => {
        if (
            !(currentParticipant.contractId in appointmentTypesByContractId) ||
            appointmentTypesByContractId[currentParticipant.contractId]?.length < 1 ||
            attendanceDetails?.length < 1
        )
            return;

        const contractAppointmentTypes =
            appointmentTypesByContractId[currentParticipant.contractId];
        const participantsAppointmentTypes = [
            ...new Set(
                rows
                    .map(
                        (el) =>
                            contractAppointmentTypes.find((entry) => entry.name === el.eventType)
                                ?.id || null
                    )
                    .filter((el) => el)
            )
        ].map((el) => contractAppointmentTypes.find((entry) => entry.id === el));

        setAppointmentTypes(participantsAppointmentTypes);

        const participantStatuses = [
            ...new Set(
                rows
                    .filter((el) => el.itemType === 'appointment')
                    .map((el) => el.eventStatus)
                    .filter((el) => el)
            )
        ]
            .map((el) => attendanceDetails.find((entry) => entry.name === el))
            .filter((el) => el);

        setAttendanceStatuses(participantStatuses);

        const usersByAppointmentType = [
            ...new Set(
                rows
                    .map((el) => {
                        const user = users.find(
                            (entry) => entry.loginEmailAddress === el.emailAddress
                        );
                        return {
                            user,
                            emailAddress: user?.loginEmailAddress || null,
                            eventType:
                                contractAppointmentTypes.find(
                                    (entry) => entry.name === el.eventType
                                )?.name || null
                        };
                    })
                    .filter((el) => el.emailAddress && el.eventType)
                    .map((el) => el.user)
            )
        ].map((el) => ({ id: el.id, name: el.loginEmailAddress }));

        setAppointmentUsers(usersByAppointmentType);
    }, [appointmentTypesByContractId, attendanceDetails, rows, users]);

    useEffect(() => {
        setIsSubmitDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (selectedTimelineFilter && 'name' in selectedTimelineFilter) {
            setType(selectedTimelineFilter.name || '');
        } else {
            setType('');
        }
    }, [selectedTimelineFilter]);

    useEffect(() => {
        setErrors({});
        if (!type) {
            setSelects({});
            return;
        }
        switch (type) {
            case 'Appointments':
                setSelects({});
                break;
            default:
                return;
        }
    }, [type]);

    // FNS
    const clearFields = () => {
        setSelects({});
        setSelectedTimelineFilter({});
        setType('');
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onFilterChange = (key, value) => {
        clearError(key);
        if (value) setSelects((prev) => ({ ...prev, [key]: value }));
        else if (!value) setSelects(trimEntries([key], selects));
    };

    const onClose = () => {
        clearFields();
        onCancel();
    };

    const onPresentAppointments = () => {
        const newErrors = validate('Appointments', selects);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        let filters;
        if (
            selects &&
            'appointmentTypeId' in selects &&
            'userId' in selects &&
            'attendanceStatusId' in selects
        ) {
            filters = '123';
        } else if (selects && 'appointmentTypeId' in selects && 'userId' in selects) {
            filters = '12';
        } else if (selects && 'appointmentTypeId' in selects && 'attendanceStatusId' in selects) {
            filters = '13';
        } else if (selects && 'userId' in selects && 'attendanceStatusId' in selects) {
            filters = '23';
        } else if (selects && 'appointmentTypeId' in selects) {
            filters = '1';
        } else if (selects && 'userId' in selects) {
            filters = '2';
        } else if (selects && 'attendanceStatusId' in selects) {
            filters = '3';
        }
        onPresentFilters(selects, filters, type);
        onClose();
    };

    const onPresent = () => {
        if (type === 'Appointments') onPresentAppointments();
    };

    // RENDER
    const renderTimelineFilterContent = () => {
        if (!type || Object.keys(appointmentTypes).length < 1) return null;
        switch (type) {
            case 'Appointments':
                return (
                    <FilterContentAppointment
                        appointmentTypes={appointmentTypes}
                        appointmentUsers={appointmentUsers}
                        attendanceStatuses={attendanceStatuses}
                        selects={selects}
                        errors={errors}
                        onFilterChange={onFilterChange}
                    />
                );
            default:
                setType('');
                return;
        }
    };

    return (
        <Dialog open={isOpen} fullWidth={true} className="muiDialogWrapper">
            <div className={modal.modalHeader}>
                <DialogTitle>Timeline Filter</DialogTitle>
            </div>
            <DialogContent>
                <form className={form.formWrapper}>
                    <div className={form.formSection}>
                        <SingleSelect
                            id="filterType"
                            label="Select Filter Type"
                            placeholder="Select filter type..."
                            disabled={!hasRole(filterRoles, loggedInUser.roles)}
                            customClass="absoluteAndHeightControlThirdLevel"
                            menuItems={timelineFilterDetails || []}
                            selectedId={selectedTimelineFilter?.id || ''}
                            selected={
                                timelineFilterDetails.find(
                                    (el) => el.id === selectedTimelineFilter?.id
                                ) || {}
                            }
                            onChange={(id) =>
                                setSelectedTimelineFilter(
                                    timelineFilterDetails.find((el) => el.id === id)
                                )
                            }
                        />
                    </div>
                    {renderTimelineFilterContent()}
                </form>

                <div className={modal.actionButtonsContainer}>
                    <div className={modal.modalActions}>
                        {type && (
                            <div className={modal.modalNoSelectError}>
                                <Button
                                    id="presentFilter"
                                    disabled={
                                        !hasRole(filterRoles, loggedInUser.roles) ||
                                        isSubmitDisabled
                                    }
                                    content="Present"
                                    error={errors?.button}
                                    clearError={() => clearError('button')}
                                    onClick={onPresent}
                                />
                            </div>
                        )}
                    </div>
                    <div className={modal.cancelButton}>
                        <Button id="cancelFilters" content="Cancel" onClick={onClose} />
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

TimelineFilterEditor.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    isOpen: PropTypes.bool,
    filterRoles: PropTypes.arrayOf(PropTypes.string),
    onCancel: PropTypes.func,
    timelineFilterDetails: PropTypes.arrayOf(PropTypes.object),
    onPresentFilters: PropTypes.func
};

export default TimelineFilterEditor;
